/**
 * Optimizer filter rules (pure). SameGameParlay.jsx turns its form into a
 * natural-language query; these helpers decide which options each dropdown
 * offers, what a change upstream clears downstream, and what text is sent.
 * Kept out of the screen so the rules are unit-tested
 * (utils/__tests__/optimizerFilters.test.js; QA 2026-10-18 D1, D3, D4, D6, D9, D13).
 */
import { sportsData, getTeams, getLeagues, getApiLeague, MARKET_CATEGORIES, TIME_FILTERS } from '../data/sportsData';

export const MAX_SGP_LEGS = 3; // the results page prices up to 3 legs (useSgpFlowData MAX_LEGS)

const lc = (s) => String(s || '').toLowerCase();
const uniqSorted = (list) => [...new Set(list.filter(Boolean))].sort();

/** Sport + display league for a league as either a display name ("Premier League") or an API code ("epl", "EPL"). */
export const resolveLeague = (league) => {
    const l = lc(league);
    if (!l) return null;
    for (const [sport, data] of Object.entries(sportsData)) {
        for (const name of data.leagues || []) {
            if (lc(name) === l || lc(data.apiLeagues?.[name]) === l) return { sport, league: name };
        }
    }
    return null;
};

/** Sport + league a team is listed under in the static data. */
export const findTeamHome = (team) => {
    if (!team) return null;
    for (const [sport, data] of Object.entries(sportsData)) {
        for (const [league, teams] of Object.entries(data.teams || {})) {
            if (teams.includes(team)) return { sport, league };
        }
    }
    return null;
};

/** A fixture's league ("nba", "NBA", "epl") matches a display league for this sport. */
export const fixtureInLeague = (fixtureLeague, sport, league) => {
    if (!league) return true;
    const f = lc(fixtureLeague);
    return f === lc(league) || f === lc(getApiLeague(sport, league));
};

/** Leagues to offer. With a sport, only that sport's leagues (cached leagues from other sports are dropped). */
export const leagueOptions = (sport, cachedLeagues = []) => {
    const cached = cachedLeagues.map((l) => resolveLeague(l) || { sport: null, league: l });
    if (sport) {
        return uniqSorted([...getLeagues(sport), ...cached.filter((c) => c.sport === sport).map((c) => c.league)]);
    }
    const all = Object.keys(sportsData).flatMap((s) => getLeagues(s));
    return uniqSorted([...all, ...cached.map((c) => c.league)]);
};

/**
 * Teams to offer. A picked game narrows to its two teams; a league to that
 * league's fixtures (or the static list); a sport to its own teams only.
 * `seedFixtures` are { league, home_team, away_team } from the mount fetch,
 * `leagueFixtures` the per-league fetch.
 */
export const teamOptions = ({ sport, league, fixture, seedFixtures = [], leagueFixtures = [] }) => {
    if (fixture) return [fixture.away_team, fixture.home_team].filter(Boolean);
    const teamsOf = (fixtures) => fixtures.flatMap((f) => [f.home_team, f.away_team]);
    if (sport && league) {
        const live = teamsOf(leagueFixtures.filter((f) => fixtureInLeague(f.league, sport, league)));
        if (live.length) return uniqSorted(live);
        return uniqSorted(getTeams(sport, league));
    }
    const seedFor = (s) => seedFixtures.filter((f) => resolveLeague(f.league)?.sport === s);
    if (sport) return uniqSorted([...getTeams(sport, null), ...teamsOf(seedFor(sport))]);
    const allStatic = Object.keys(sportsData).flatMap((s) => getTeams(s, null));
    return uniqSorted([...allStatic, ...teamsOf(seedFixtures)]);
};

/** Markets to offer: the sport's catalog plus cached markets seen on that sport's fixtures, narrowed by category keywords. */
export const marketOptions = ({ sport, category, sportMarkets = {}, seedFixtures = [], fallback = () => [] }) => {
    const cachedFor = (s) => seedFixtures
        .filter((f) => !s || resolveLeague(f.league)?.sport === s)
        .flatMap((f) => (f.markets && typeof f.markets === 'object' ? Object.keys(f.markets) : []));
    let base;
    if (!sport) {
        base = Object.values(sportMarkets).flat();
        if (!base.length) base = Object.keys(sportsData).flatMap((s) => fallback(s));
    } else {
        base = (sportMarkets[sport] && sportMarkets[sport].length) ? sportMarkets[sport] : fallback(sport);
    }
    const all = uniqSorted([...base, ...cachedFor(sport || null)]);
    const keywords = category && MARKET_CATEGORIES[category];
    if (!keywords) return all;
    return all.filter((m) => keywords.some((kw) => lc(m).includes(kw)));
};

export const isPlayerMarket = (market) => (MARKET_CATEGORIES['Player Props'] || []).some((kw) => lc(market).includes(kw));

/** Epoch ms for a start time sent as ISO text, ms, or a serialized Firestore Timestamp ({ _seconds }). */
export const fixtureTime = (v) => {
    if (v == null || v === '') return NaN;
    if (typeof v === 'number') return v;
    if (typeof v === 'string') return Date.parse(v);
    const sec = v._seconds ?? v.seconds;
    return typeof sec === 'number' ? sec * 1000 : NaN;
};

/** Games for the picked league, soonest first, labelled with their date so repeat matchups differ. */
export const fixtureOptions = (fixtures, sport, league, formatDate = defaultDate) => {
    const at = (f) => { const ms = fixtureTime(f.start_date); return Number.isFinite(ms) ? ms : Infinity; };
    const list = fixtures
        .filter((f) => fixtureInLeague(f.league, sport, league))
        .slice()
        .sort((a, b) => at(a) - at(b));
    const seen = new Set();
    return list.map((f) => {
        const ms = fixtureTime(f.start_date);
        const date = Number.isFinite(ms) ? formatDate(new Date(ms).toISOString()) : '';
        let label = `${f.away_team} @ ${f.home_team}${date ? ` · ${date}` : ''}`;
        if (seen.has(label)) label = `${label} (${String(f.id).slice(-4)})`;
        seen.add(label);
        return { ...f, label };
    });
};

function defaultDate(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Changing the sport: drop anything that belonged to the old one (or everything, when the sport is cleared). */
export const sportChange = (state, sport) => {
    if (!sport) {
        return { sport: '', league: '', team: '', fixture: null, player: '', market: '', category: '', line: '', direction: '' };
    }
    if (sport === state.sport) return {};
    // First pick keeps what was typed; switching from another sport drops its player and market.
    const patch = state.sport ? { sport, player: '', market: '', category: '', line: '', direction: '' } : { sport };
    if (state.league && !getLeagues(sport).includes(state.league)) Object.assign(patch, { league: '', team: '', fixture: null });
    const home = findTeamHome(state.team);
    if (state.team && home && home.sport !== sport) Object.assign(patch, { team: '', fixture: null });
    if (state.fixture) patch.fixture = null;
    return patch;
};

/** Changing the league: set its sport, and clear the team and game when they no longer fit. */
export const leagueChange = (state, league) => {
    if (!league) return { league: '', team: '', fixture: null };
    const found = resolveLeague(league);
    const patch = { league: found?.league || league };
    if (found && found.sport !== state.sport) Object.assign(patch, sportChange(state, found.sport), { league: found.league });
    if (state.fixture && !fixtureInLeague(state.fixture.league, found?.sport || state.sport, patch.league)) patch.fixture = null;
    const teams = getTeams(found?.sport || state.sport, patch.league);
    if (state.team && teams.length && !teams.includes(state.team)) Object.assign(patch, { team: '', fixture: null });
    return patch;
};

/** A line value as typed: '' (none), a normalized number string, or null (invalid). */
export const parseLine = (raw) => {
    const s = String(raw ?? '').trim();
    if (!s) return '';
    if (!/^[+-]?(\d+(\.\d+)?|\.\d+)$/.test(s)) return null;
    const n = Number(s);
    return Number.isFinite(n) ? String(n) : null;
};

/** Problem with the line + direction pair, or '' when fine. */
export const lineProblem = (raw, direction) => {
    const v = parseLine(raw);
    if (v === null) return 'Line must be a number, like 25.5 or -3.5.';
    if (direction && v === '') return `Add a line to bet ${direction.toLowerCase()}.`;
    if (direction && Number(v) < 0) return 'Over/Under lines can’t be negative.';
    return '';
};

/**
 * The text for one search or leg. In a same-game parlay the game is said once
 * for the whole slip (sgpQuery), so legs leave out the league.
 */
export const describeSelection = ({ sport, league, team, player, market, line, direction, timeFilter, sportsbooks = [] }, { forLeg = false } = {}) => {
    const parts = [];
    if (!forLeg) {
        if (league) parts.push(league);
        else if (sport) parts.push(sport);
    }
    if (team) parts.push(team);
    if (player) parts.push(player);
    if (market && lc(market) !== 'all') parts.push(market);
    const v = parseLine(line);
    if (v) parts.push(direction ? `${direction} ${v}` : v);
    if (!forLeg && timeFilter) {
        const t = TIME_FILTERS.find((x) => x.value === timeFilter)?.label;
        if (t && t !== 'All Games') parts.push(lc(t));
    }
    if (!forLeg && sportsbooks.length === 1) parts.push(sportsbooks[0]);
    return parts.join(' ');
};

/** "NBA Boston Celtics @ Detroit Pistons Moneyline + Jayson Tatum Player Points Over 25.5 sgp" */
export const sgpQuery = (fixture, league, legs) => {
    if (!legs.length) return '';
    const game = fixture ? `${league ? `${league} ` : ''}${fixture.away_team} @ ${fixture.home_team}` : league;
    return [game, legs.map((l) => l.description).join(' + '), 'sgp'].filter(Boolean).join(' ');
};

/** Why a leg can't be added yet, or '' when it can. */
export const legProblem = ({ fixture, team, player, market, legs }) => {
    if (!fixture) return 'Pick a game first. Every leg in a same-game parlay comes from one game.';
    if (legs.length >= MAX_SGP_LEGS) return `A parlay here can have up to ${MAX_SGP_LEGS} legs.`;
    if (!market && !team && !player) return 'Pick a market, team or player for this leg.';
    if (market && isPlayerMarket(market) && !player) return `Pick a player for ${market}.`;
    return '';
};

/** getPlayersCache search keyword for a team: the nickname, or two words when the last one is shared ("Red Sox", "Man United"). */
const SHARED_LAST = new Set(['sox', 'united', 'city', 'fc', 'sc', 'state', 'athletic']);
export const teamSearchKeyword = (team) => {
    const words = String(team || '').trim().split(/\s+/).filter(Boolean);
    if (words.length < 2) return words[0] || '';
    return SHARED_LAST.has(lc(words[words.length - 1])) ? words.slice(-2).join(' ') : words[words.length - 1];
};

/** Player field's empty state, naming the team when one is picked ("No Milwaukee Brewers player matches “caminero”"). */
export const playerEmptyText = (team) => (typed) => {
    const t = team && lc(team) !== 'all' ? `${team} ` : '';
    return `No ${t}player matches “${typed}”`;
};
